import './styles.scss';
import { Select } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import cx from 'classnames';
import { ErrorMessage, useField, useFormikContext } from 'formik';
import React, { HTMLAttributes, useEffect } from 'react';
import { Input } from 'reactstrap';
import styles from './styles.modules.scss';

type FieldComponentWithProps<
  T extends React.ComponentType<any>
> = T extends React.ComponentType<infer P> ? P : never;

type FieldType = typeof Input | typeof TextArea | typeof Select | 'input';

type FieldProps<T extends FieldType> = Omit<
  T extends React.ComponentType<any>
    ? FieldComponentWithProps<T>
    : HTMLAttributes<HTMLInputElement>,
  'onFocus'
> & {
  component?: T;
  label?: string;
  name: string;
  className?: string;
  defaultValue?: any;
  hidden?: boolean;
};

const Field = <T extends FieldType = 'input'>({
  component: Component = 'input',
  label,
  name,
  defaultValue,
  className,
  hidden,
  ...restProps
}: FieldProps<T>) => {
  const type =
    (restProps as any).type === 'number' ||
    (restProps as any).type === 'checkbox'
      ? (restProps as any).type
      : undefined;
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField({
    name,
    type,
    value:
      (restProps as any).type === 'checkbox'
        ? (restProps as any).value
        : undefined,
  });
  const hiddenStyleName = cx({
    hidden,
  });

  useEffect(() => {
    if (defaultValue) {
      setFieldValue(name, defaultValue);
    }
  }, []);

  return (
    <>
      {label && (
        <label styleName={hiddenStyleName} htmlFor={name}>
          {label}
        </label>
      )}
      <Component
        id={name}
        className={cx(
          'form-control',
          {
            'is-invalid': meta.touched && meta.error,
            [styles.hidden]: hidden,
          },
          className
        )}
        {...field}
        {...(restProps as any)}
        onChange={(e: any) => {
          if (!e?.persist) {
            setFieldValue(name, e);
            return;
          }

          field.onChange(e);
        }}
        onBlur={(e: any) => {
          if (!e?.persist) {
            setFieldValue(name, e);
            return;
          }

          field.onBlur(e);
        }}
      />
      <ErrorMessage
        styleName={hiddenStyleName}
        component="div"
        name={name}
        className="invalid-feedback"
      />
    </>
  );
};

export default Field;
